/* global google */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  BicyclingLayer,
  GoogleMap,
  TrafficLayer,
  TransitLayer,
  useJsApiLoader
} from '@react-google-maps/api';

import { mapConfig } from '@constants/component-configs';
import { googleMapsApiKey, libraries, version } from '@constants/google-maps';
import { getDefaultMapStylers } from '@constants/config';

import { getCurrentBicycle, getCurrentTraffic, getCurrentTransit, getCurrentViewport } from '@selectors/map-selector';

import './dotmaps-google-map.scss';

const SATELLITE_MAP_TYPE_IDS = ['satellite', 'hybrid'];

const DotMapsGoogleMap = ({
  components,
  onBoundsChanged,
  onClick,
  onDblClick,
  onDragEnd,
  onDragStart,
  onIdle,
  onMapLoad,
  onMouseMove
}) => {
  const mapTypeId = useSelector(state => state.map.mapTypeId);
  const bicycle = useSelector(getCurrentBicycle);
  const traffic = useSelector(getCurrentTraffic);
  const transit = useSelector(getCurrentTransit);
  const { center, zoom } = useSelector(getCurrentViewport);

  const [mapOptions, setMapOptions] = useState(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    setMapOptions({styles: getDefaultMapStylers(), ...mapConfig.mapOptions});
  }, []);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey,
    libraries,
    version
  });

  const handleMapLoad = useCallback(mapInstance => {
    mapInstanceRef.current = mapInstance;
    if (onMapLoad) {
      onMapLoad(mapInstance);
    }
  }, [onMapLoad]);

  const handleMapUnmount = useCallback(() => {
    mapInstanceRef.current = null;
  }, []);

  // Satellite/hybrid imagery has a per-location zoom ceiling (it's photographed, not generated).
  // As of Maps JS API v3.62 the map no longer auto-falls back to 45deg imagery past that ceiling,
  // so zooming past it just shows Google's own "Sorry, we have no imagery here." placeholder
  // instead of blocking the zoom. google.maps.MaxZoomService (the API meant to predict this
  // ceiling ahead of time) was tried and found to return inaccurate ceilings in practice (e.g.
  // reporting zoom 21 as safe when real imagery cut off at 17) - so instead of predicting, this
  // reacts to the actual placeholder (Google renders it as an unstyled div with no class/id, so
  // text content is the only stable signal), and Google never removes old placeholder nodes from
  // the DOM once shown, so presence alone can't tell "still failing" apart from "an old, unrelated
  // one is just lying around". Instead this tracks the COUNT of matching occurrences in the map's
  // DOM: a rising count means a genuinely new placeholder just appeared for the current view, an
  // unchanged count means nothing new happened (whether that's because it's already resolved, or
  // because old ones are still sitting there stale). Each rise steps back one zoom level and
  // re-checks - jumping into satellite/hybrid from a much deeper starting zoom (e.g. Default's
  // max) can be many levels past the real ceiling, so a single step isn't always enough, and this
  // keeps stepping back until a step produces no new occurrence. The cap only lifts on a real drag
  // (dragend) - not on distance moved, since that was tried and rejected: zoom actions alone can
  // shift the reported center too (scroll-wheel zoom recenters toward the cursor) with no real
  // relocation, but satellite resolution can genuinely differ between two nearby points, so no
  // fixed distance threshold could tell those apart correctly.
  // Deliberately conservative: only reads/mutates the LIVE map instance directly (never through
  // the mapOptions React state/options prop, to avoid re-triggering a full re-send of the base
  // options blob while mid-switch into satellite/hybrid, which previously caused map-type
  // switching itself to break), and any failure here is swallowed so it can never break map-type
  // switching.
  useEffect(() => {
    let cleanup;
    const mapInstance = isLoaded ? mapInstanceRef.current : null;

    if (mapInstance && !SATELLITE_MAP_TYPE_IDS.includes(mapTypeId)) {
      try {
        mapInstance.setOptions({ maxZoom: null });
      } catch {
        // Swallow: must never break map-type switching.
      }
    } else if (mapInstance) {
      // A single native textContent read on the container already recursively concatenates all
      // descendant text in one pass - cheap even with many tiles, since tiles are images/divs
      // with no text of their own. Counting occurrences is then a plain string scan over that one
      // small string, not another DOM walk.
      const countNoImageryOccurrences = () => {
        const mapDiv = mapInstance.getDiv();
        const text = mapDiv ? (mapDiv.textContent || '').toLowerCase() : '';
        const needle = 'no imagery';
        let count = 0;
        let index = text.indexOf(needle);
        while (index !== -1) {
          count += 1;
          index = text.indexOf(needle, index + needle.length);
        }
        return count;
      };

      // Snapshot BEFORE the first real check so any placeholder occurrences already sitting in
      // the DOM from earlier, unrelated zooming this session are never mistaken for a fresh one.
      let baselineCount = countNoImageryOccurrences();

      // Fires on every DOM change inside the map (tile loads, pans, the switch into satellite
      // itself, etc.) so it catches the placeholder the instant Google actually inserts it -
      // unlike 'idle', which doesn't reliably re-fire right after a plain map-type switch, so a
      // switch straight into satellite/hybrid while already zoomed in could show the placeholder
      // and never get corrected until some later zoom action happened to trigger 'idle' again.
      const correctForPlaceholder = () => {
        try {
          const currentCount = countNoImageryOccurrences();
          if (currentCount <= baselineCount) {
            return;
          }
          baselineCount = currentCount;

          const currentZoom = mapInstance.getZoom();
          if (typeof currentZoom !== 'number') {
            return;
          }

          const safeZoom = currentZoom - 1;
          mapInstance.setOptions({ maxZoom: safeZoom });
          mapInstance.setZoom(safeZoom);
        } catch {
          // Swallow: must never break satellite/hybrid mode.
        }
      };

      // Only a real drag means the user deliberately moved to a different spot - lift the cap so
      // a different, possibly better-covered area can be probed fresh, and refresh the baseline so
      // whatever's already on screen there isn't mistaken for a fresh failure either.
      const handleDragEnd = () => {
        try {
          mapInstance.setOptions({ maxZoom: null });
          baselineCount = countNoImageryOccurrences();
        } catch {
          // Swallow.
        }
      };

      const idleListener = google.maps.event.addListener(mapInstance, 'idle', correctForPlaceholder);
      const dragEndListener = google.maps.event.addListener(mapInstance, 'dragend', handleDragEnd);

      let observer = null;
      const mapDiv = mapInstance.getDiv();
      if (mapDiv && typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(correctForPlaceholder);
        try {
          // Switching map type while already zoomed in reuses/updates existing DOM (text and/or
          // attribute changes on an existing node) rather than inserting a fresh one the way a new
          // tile at a new zoom level does - childList alone misses that, so watch text/attribute
          // changes too.
          observer.observe(mapDiv, {
            attributes: true,
            characterData: true,
            childList: true,
            subtree: true
          });
        } catch {
          observer = null;
        }
      }

      // Belt-and-braces: switching map type can trigger the placeholder to appear slightly after
      // this effect runs (Google still has to attempt the tile load), with no guarantee that
      // covers every timing/DOM-shape case above. A couple of short, cheap re-checks close that
      // gap without needing to know exactly how/when Google renders it in this scenario.
      const fallbackTimeouts = [200, 600, 1500, 3000].map(delay => setTimeout(correctForPlaceholder, delay));

      cleanup = () => {
        try {
          google.maps.event.removeListener(idleListener);
          google.maps.event.removeListener(dragEndListener);
        } catch {
          // Swallow.
        }
        if (observer) {
          try {
            observer.disconnect();
          } catch {
            // Swallow.
          }
        }
        fallbackTimeouts.forEach(clearTimeout);
      };
    }

    return cleanup;
  }, [isLoaded, mapTypeId]);

  if (isLoaded) {
    const containerStyle = { width: '100%', height: '100%' };
    return (
      <div data-testid="google-map" styleName="map-wrapper">
        <div styleName="map-element">
          <GoogleMap
            center={center}
            mapContainerStyle={containerStyle}
            mapTypeId={mapTypeId}
            onBoundsChanged={onBoundsChanged}
            onClick={onClick}
            onDblClick={onDblClick}
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            onIdle={onIdle}
            onLoad={handleMapLoad}
            onMouseMove={onMouseMove}
            onUnmount={handleMapUnmount}
            options={mapOptions}
            zoom={zoom}
          >
            {traffic && <TrafficLayer />}
            {bicycle && <BicyclingLayer />}
            {transit && <TransitLayer />}
            {components}
          </GoogleMap>
        </div>
      </div>
    );
  }
  return null;
};

DotMapsGoogleMap.propTypes = {
  components: PropTypes.node,
  onBoundsChanged: PropTypes.func,
  onClick: PropTypes.func,
  onDblClick: PropTypes.func,
  onDragEnd: PropTypes.func,
  onDragStart: PropTypes.func,
  onIdle: PropTypes.func,
  onMapLoad: PropTypes.func,
  onMouseMove: PropTypes.func
};

export default memo(DotMapsGoogleMap);
